import { Icon } from "./Icon";
import type { IconName } from "./icons/paths";

/**
 * Круглая иконка с ореолом — `.round-icon` из пакета дизайна.
 *
 * Устройство, ради которого он и вынесен в примитив: цвет задаётся ОДИН раз,
 * свойством `color` на обёртке, а ореол, граница и сама иконка читают
 * `currentColor`. Поэтому тонов пять, а цветовых правил — ноль.
 *
 * Ореол — дочерний span, а не `::before`, как в макете: он опционален
 * (`glow`), а условных псевдоэлементов не бывает.
 */
export type RoundIconTone = "green" | "cyan" | "violet" | "gold" | "rose" | "neutral";

const TONES: Record<RoundIconTone, string> = {
  green: "text-status-profit",
  cyan: "text-status-info",
  violet: "text-ai-violet",
  gold: "text-status-warning",
  rose: "text-status-loss",
  neutral: "text-text-secondary",
};

interface RoundIconProps {
  icon: IconName;
  /** Диаметр в пикселях — берётся из макета, поэтому число, а не класс. */
  size?: number;
  tone?: RoundIconTone;
  /** Ореол вокруг кружка. Выключается там, где иконок много в столбик. */
  glow?: boolean;
  className?: string;
}

export function RoundIcon({
  icon,
  size = 30,
  tone = "green",
  glow = true,
  className,
}: RoundIconProps) {
  return (
    <span
      className={[
        "relative flex shrink-0 items-center justify-center rounded-full border border-border-subtle",
        "bg-[image:var(--surface-round-icon)]",
        TONES[tone],
        className ?? "",
      ].join(" ")}
      style={{ width: size, height: size }}
    >
      {glow ? <span aria-hidden="true" className="halo absolute -inset-1 rounded-full" /> : null}
      {/* 55% диаметра — пропорция из макета; иконка внутри кружка не должна
          доходить до границы, иначе ореол сливается с рисунком. */}
      <Icon name={icon} size={Math.round(size * 0.55)} />
    </span>
  );
}
